import apiClient from '../api.js';
import store from '@/store/index.js';

// Capa de servicios del módulo "Gestión de Accesos al Sistema".
// Consume los endpoints ya implementados y testeados en `UNICEN/modules/accesos/`
// (ver su README.md, que es la fuente de verdad del contrato). Este archivo NO
// decide la forma de ningún payload: la refleja tal cual está documentada.
//
// ── Contrato asumido ───────────────────────────────────────────────────────────
//   Header obligatorio en TODAS las llamadas (incluidos los catálogos):
//     x-usuario-id: <id_usuario del gestor>   → en el store de sesión es `user.personal`
//     Sin header / id inexistente / gestor INACTIVO → 401
//     Rol de nivel < 50                              → 403
//
//   GET /accesos/contexto
//     -> { data: { id_usuario, unicodigo, nombre_completo, id_sede, sede_nombre,
//                  id_rol, rol_nombre, nivel, puede_seleccionar_sede,
//                  puede_cambiar_sede, nivel_gestor_minimo } }
//
//   GET /accesos/usuarios?estado=ACTIVO|INACTIVO&id_rol=&nombre=&id_sede=(solo nivel 100)
//                        &pagina=&limite=(tope 100)&orden=unicodigo|nombre|rol|estado
//                        &direccion=asc|desc
//     -> { data: { items: [{ id_usuario, unicodigo, nombre_completo, carnet, id_rol,
//                            rol_nombre, nivel_rol, estado, id_sede, sede_nombre,
//                            tiene_accesos_multiples }],
//                  total, pagina, limite, id_sede } }
//     `nombre` matchea nombre completo, unicódigo y carnet a la vez.
//     Devuelve una fila por ACCESO, no por persona.
//
//   GET /accesos/personal-sin-acceso?estado=INACTIVO|NO_REGISTRADO&nombre=&id_sede=
//                                   &pagina=&limite=&orden=unicodigo|nombre|fregistro
//                                   &direccion=asc|desc
//     -> { data: { items: [{ unicodigo, nombre_completo, carnet, id_sede, sede_nombre,
//                            fecha_registro, estado_acceso, id_usuario, id_rol,
//                            rol_nombre }], total, pagina, limite, id_sede } }
//     `id_usuario`/`id_rol`/`rol_nombre` vienen SOLO en las filas `INACTIVO`:
//     son los datos del acceso a reactivar. En `NO_REGISTRADO` van en null.
//
//   GET /accesos/roles?id_sede=(solo nivel 100)
//     -> { data: [{ id_rol, nombre, id_sede, nivel, asignable }] }
//     Devuelve TODOS los roles de la sede: la misma llamada alimenta el filtro
//     (donde tienen que estar todos) y el select de asignación (donde los
//     `asignable: false` van deshabilitados).
//
//   GET /accesos/sedes  -> { data: [{ id_sede, nombre, sigla, departamento }] }
//     Las tres para nivel 100; solo la propia para el resto.
//
//   POST  /accesos/usuarios              body { unicodigo, id_rol }        -> 201 { data }
//     `id_sede` NO se acepta: mandarlo devuelve 400 a propósito (la sede se
//     hereda de `personal.id_sede`).
//   PATCH /accesos/usuarios/:id/rol      body { id_rol }                   -> { data }
//   PATCH /accesos/usuarios/:id/estado   body { activo: 'ACTIVO'|'INACTIVO', motivo? }
//     -> { data: { ..., estado, sin_cambios, otros_accesos_activos: [...] } }
//   PATCH /accesos/usuarios/:id/sede     body { id_sede }                  -> { data }
//
//   Menús por rol (solo nivel 100: `contexto.puede_gestionar_menus`):
//   GET   /accesos/menus?id_sede=
//     -> { data: { id_sede, componentes: [{ id_componente, nombre, descrip, nombre_menu,
//                  systemseiko, icon, path }], roles: [{ id_rol, nombre, usuarios_activos }],
//                  asignaciones: [{ id_rol, id_componente }] } }
//   PATCH /accesos/menus  body { id_sede, cambios: [{ id_rol, id_componente, asignado }] }
//     -> { data: { id_sede, asignados, quitados, sin_cambios } }   (todo o nada, tope 200)
//
// ── Errores ───────────────────────────────────────────────────────────────────
//   Forma: { mensaje: "...", codigo: "...", ...campos extra }
//   `mensaje` es para mostrar; `codigo` es para decidir. NUNCA hacer string
//   matching sobre el mensaje en español. Los tres 409 que la UI distingue traen
//   campos extra para las recuperaciones guiadas:
//     ACCESO_DUPLICADO             —
//     ACCESO_INACTIVO_EXISTENTE    id_usuario, id_rol
//     ROL_NO_DISPONIBLE_EN_SEDE    rol_nombre, id_sede_destino, sede_destino_nombre

const resource = '/accesos';

/**
 * El `id_usuario` del gestor que opera. En el store de sesión el campo se llama
 * `personal` (sí, `personal`) y contiene el `id_usuario` de `unicen.usuario`.
 *
 * Se lee del store y, si todavía no se hidrató (recarga dura de la página: el
 * `setUser` de App.vue corre en su `mounted`, que puede ser posterior al
 * `created` de una vista de ruta), del `sessionStorage`, que es de donde el
 * propio App.vue lo restaura.
 */
function obtenerIdUsuarioGestor() {
  const usuario = store.getters.user;
  if (usuario && usuario.personal !== undefined && usuario.personal !== null) {
    return usuario.personal;
  }
  try {
    const sesion = JSON.parse(sessionStorage.getItem('session'));
    return sesion && sesion.personal !== undefined ? sesion.personal : null;
  } catch (e) {
    return null;
  }
}

/**
 * Saca del query string las claves vacías. El backend tolera `estado=`, pero una
 * URL sin parámetros muertos es la que se puede pegar en el navegador para
 * reproducir un bug.
 */
function limpiarParametros(parametros) {
  const limpios = {};
  Object.keys(parametros || {}).forEach((clave) => {
    const valor = parametros[clave];
    if (valor !== null && valor !== undefined && valor !== '') limpios[clave] = valor;
  });
  return limpios;
}

function construirConfig(parametros) {
  const config = { headers: { 'x-usuario-id': obtenerIdUsuarioGestor() } };
  if (parametros) config.params = limpiarParametros(parametros);
  return config;
}

const useAccesosApi = {
  // ── Contexto y catálogos ─────────────────────────────────────────────
  obtenerContexto() {
    return apiClient.get(`${resource}/contexto`, construirConfig());
  },
  listarSedes() {
    return apiClient.get(`${resource}/sedes`, construirConfig());
  },
  listarRoles(parametros) {
    return apiClient.get(`${resource}/roles`, construirConfig(parametros || {}));
  },

  // ── Listados ─────────────────────────────────────────────────────────
  listarUsuarios(parametros) {
    return apiClient.get(`${resource}/usuarios`, construirConfig(parametros || {}));
  },
  listarPersonalSinAcceso(parametros) {
    return apiClient.get(`${resource}/personal-sin-acceso`, construirConfig(parametros || {}));
  },

  // ── Acciones ─────────────────────────────────────────────────────────
  // `id_sede` no se envía nunca en el alta: el backend lo rechaza con 400.
  crearAcceso(datos) {
    return apiClient.post(`${resource}/usuarios`, datos, construirConfig());
  },
  cambiarRol(idUsuario, datos) {
    return apiClient.patch(`${resource}/usuarios/${idUsuario}/rol`, datos, construirConfig());
  },
  cambiarEstado(idUsuario, datos) {
    return apiClient.patch(`${resource}/usuarios/${idUsuario}/estado`, datos, construirConfig());
  },
  cambiarSede(idUsuario, datos) {
    return apiClient.patch(`${resource}/usuarios/${idUsuario}/sede`, datos, construirConfig());
  },

  // ── Menús por rol ────────────────────────────────────────────────────
  obtenerMenus(parametros) {
    return apiClient.get(`${resource}/menus`, construirConfig(parametros || {}));
  },
  guardarMenus(datos) {
    return apiClient.patch(`${resource}/menus`, datos, construirConfig());
  },
};

// ── Helpers de error ───────────────────────────────────────────────────

/** El `codigo` de máquina del error, o null si la respuesta no lo trae. */
function codigoDeError(error) {
  if (!error || !error.response || !error.response.data) return null;
  return error.response.data.codigo || null;
}

/**
 * El cuerpo completo del error, con los campos extra aplanados
 * (`id_usuario`, `id_rol`, `sede_destino_nombre`, ...). Devuelve `{}` si no hay.
 */
function datosDeError(error) {
  if (!error || !error.response || !error.response.data) return {};
  return error.response.data;
}

function estadoHttpDeError(error) {
  if (!error || !error.response) return null;
  return error.response.status;
}

/** 401: el gestor dejó de ser válido. Obliga a relogin, no a reintentar. */
function esSesionInvalida(error) {
  return estadoHttpDeError(error) === 401;
}

/**
 * 404: para el backend significa "no existe" O "es de otra sede", con el mismo
 * código a propósito. Lo que la UI tiene que hacer en los dos casos es lo mismo:
 * cerrar el diálogo y recargar el listado, porque lo que creía sobre esa fila ya
 * no es verdad.
 */
function esRecursoNoDisponible(error) {
  return estadoHttpDeError(error) === 404;
}

// Piso de mensajes por código. El mensaje del backend, cuando viene, GANA sobre
// estos en 400/403/409: son el piso, no un reemplazo.
const MENSAJE_POR_CODIGO = {
  AUTO_GESTION_PROHIBIDA:
    'No podés modificar tu propio acceso. Otro gestor autorizado tiene que hacer el cambio.',
  PERMISOS_INSUFICIENTES: 'No tenés permiso para realizar esta acción.',
  ACCESO_DUPLICADO: 'Esta persona ya tiene un acceso activo en el sistema.',
  ACCESO_INACTIVO_EXISTENTE:
    'Esta persona ya tuvo un acceso y hoy está inactivo. Corresponde reactivarlo, no crear uno nuevo.',
  USUARIO_INACTIVO: 'El acceso está inactivo. Reactivalo antes de hacer este cambio.',
  ROL_NO_DISPONIBLE_EN_SEDE:
    'El rol de esta persona no existe en la sede destino, así que no se puede trasladar conservándolo.',
  ACCESOS_MULTIPLES:
    'Esta persona tiene más de un acceso en el sistema. Hay que unificarlos antes de moverla de sede.',
  AUTO_BLOQUEO_MENU:
    'No podés quitarle a tu propio rol la pantalla de gestión de accesos.',
};

/**
 * Traduce un error de axios al mensaje que se le muestra al gestor.
 *
 * `accion` es un infinitivo corto que se usa solo en el mensaje genérico de 500,
 * por ejemplo 'crear el acceso' o 'cambiar el rol'.
 *
 * Criterios (§7.2 del plan de UX):
 * - El 404 NUNCA menciona sedes: el backend lo usa también para "es de otra
 *   sede", y decirlo filtraría justo la información que el épico manda ocultar.
 * - En 400/403/409 el mensaje del backend gana, porque es el que conoce el caso
 *   concreto (qué rol, qué sede).
 */
function mensajeDeErrorDeAccesos(error, accion = 'realizar la acción') {
  if (!error || !error.response) {
    return 'No pudimos conectarnos con el servidor. Revisá tu conexión e intentá de nuevo.';
  }

  const estado = error.response.status;
  const cuerpo = error.response.data || {};
  const delBackend =
    typeof cuerpo.mensaje === 'string' && cuerpo.mensaje.trim() ? cuerpo.mensaje.trim() : null;
  const porCodigo = MENSAJE_POR_CODIGO[cuerpo.codigo] || null;

  if (estado === 401) {
    return 'Tu sesión ya no es válida. Iniciá sesión de nuevo para continuar.';
  }
  if (estado === 404) {
    return 'Ese acceso ya no está disponible. Actualizamos la lista para mostrarte el estado real.';
  }
  if (estado === 400) {
    return (
      delBackend ||
      porCodigo ||
      'Los datos enviados no son válidos. Revisá el rol seleccionado e intentá de nuevo.'
    );
  }
  if (estado === 403 || estado === 409) {
    return delBackend || porCodigo || 'No tenés permiso para realizar esta acción.';
  }
  return `Ocurrió un error inesperado al ${accion}. Si vuelve a pasar, avisá a Plataforma.`;
}

export {
  mensajeDeErrorDeAccesos,
  codigoDeError,
  datosDeError,
  estadoHttpDeError,
  esSesionInvalida,
  esRecursoNoDisponible,
};
export default useAccesosApi;
